// car modal component
import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const CarModal = ({ car, onClose }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!car) return null;

  const selectedCar = car;
  const setSelectedCar = onClose;

  return (
    <div
      className="fixed inset-0 bg-blackC/90 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={() => setSelectedCar(null)}
    >
      <div
        className="bg-dark border border-gray-dark rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl shadow-black/50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col lg:flex-row h-full">
          {/* Left Panel - Image Gallery */}
          <div className="lg:w-[55%] relative bg-charcoal/30">
            {/* Close Button - Floating */}
            <button
              onClick={() => setSelectedCar(null)}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-blackC/60 backdrop-blur-sm border border-white/10 flex items-center justify-center text-gray-light hover:text-cream hover:bg-blackC/80 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Main Image */}
            {selectedCar.images && selectedCar.images.length > 0 ? (
              <div className="relative h-72 lg:h-full min-h-[400px]">
                {/* Gradient Overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-dark via-transparent to-transparent z-10 lg:hidden" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-dark/30 z-10 hidden lg:block" />

                <img
                  src={selectedCar.images[currentImageIndex].url}
                  alt={selectedCar.name}
                  className="w-full h-full object-cover"
                />

                {/* Image Counter Badge */}
                {selectedCar.images.length > 1 && (
                  <div className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-blackC/60 backdrop-blur-sm border border-white/10 px-3 py-1.5 rounded-full">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-cream text-sm font-medium">{currentImageIndex + 1} / {selectedCar.images.length}</span>
                  </div>
                )}

                {/* Navigation Arrows */}
                {selectedCar.images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex((prev) => (prev === 0 ? selectedCar.images.length - 1 : prev - 1));
                      }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-blackC/40 backdrop-blur-sm border border-white/10 text-cream hover:bg-primary hover:text-blackC hover:border-primary transition-all flex items-center justify-center group"
                    >
                      <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex((prev) => (prev === selectedCar.images.length - 1 ? 0 : prev + 1));
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-blackC/40 backdrop-blur-sm border border-white/10 text-cream hover:bg-primary hover:text-blackC hover:border-primary transition-all flex items-center justify-center group"
                    >
                      <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}

                {/* Thumbnail Strip */}
                {selectedCar.images.length > 1 && (
                  <div className="absolute bottom-4 left-4 right-4 z-20">
                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                      {selectedCar.images.map((image, idx) => (
                        <button
                          key={image._id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentImageIndex(idx);
                          }}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${currentImageIndex === idx
                            ? 'border-primary ring-2 ring-primary/30 scale-105'
                            : 'border-white/20 hover:border-white/40 opacity-70 hover:opacity-100'
                            }`}
                        >
                          <img
                            src={image.url}
                            alt={`${selectedCar.name} ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-72 lg:h-full min-h-[400px] flex items-center justify-center bg-charcoal">
                <svg className="w-24 h-24 text-gray-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>

          {/* Right Panel - Details */}
          <div className="lg:w-[45%] flex flex-col max-h-[90vh] lg:max-h-full">
            {/* Header */}
            <div className="p-6 lg:p-8 border-b border-gray-dark">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-2.5 py-1 text-xs font-medium rounded-md bg-primary/10 text-primary border border-primary/20 uppercase tracking-wider">
                      {selectedCar.type}
                    </span>
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-md ${selectedCar.isAvailable
                      ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                      : 'bg-red-500/10 text-red-400 border border-red-500/20'
                      }`}>
                      {selectedCar.isAvailable
                        ? (language === 'en' ? 'Available' : 'متاح')
                        : (language === 'en' ? 'Unavailable' : 'غير متاح')}
                    </span>
                  </div>
                  <h2 className="font-display text-3xl text-cream">{selectedCar.name}</h2>
                  <p className="text-gray-silver mt-1">{selectedCar.model}</p>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 lg:p-8 space-y-6">
              {/* Capacity Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-charcoal/50 border border-gray-dark rounded-xl p-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 mx-auto mb-3 flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-silver text-xs uppercase tracking-wider mb-1">{t.passengers}</p>
                  <p className="text-cream font-display text-2xl">{selectedCar.passengerCapacity}</p>
                </div>
                <div className="bg-charcoal/50 border border-gray-dark rounded-xl p-4 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 mx-auto mb-3 flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-silver text-xs uppercase tracking-wider mb-1">{t.luggage}</p>
                  <p className="text-cream font-display text-2xl">{selectedCar.luggageCapacity} <span className="text-base text-gray-light">{t.bags}</span></p>
                </div>
              </div>

              {/* Pricing Card */}
              <div className="relative overflow-hidden rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 via-transparent to-primary/10">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />

                <div className="relative p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-gray-light text-sm uppercase tracking-wider">{language === 'en' ? 'Pricing' : 'التسعير'}</span>
                  </div>

                  {selectedCar.pricingType === 'fixed' && selectedCar.fixedPrice && (
                    <div>
                      <p className="text-gray-silver text-sm mb-1">{language === 'en' ? 'Fixed Rate' : 'سعر ثابت'}</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-primary font-display text-4xl">{selectedCar.fixedPrice.toLocaleString()}</span>
                        <span className="text-gray-light text-lg">{language === 'en' ? 'EGP' : 'جنيه'}</span>
                      </div>
                    </div>
                  )}
                  {selectedCar.pricingType === 'perKm' && selectedCar.pricePerKm && (
                    <div>
                      <p className="text-gray-silver text-sm mb-1">{language === 'en' ? 'Per Kilometer' : 'لكل كيلومتر'}</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-primary font-display text-4xl">{selectedCar.pricePerKm.toLocaleString()}</span>
                        <span className="text-gray-light text-lg">{language === 'en' ? 'EGP/km' : 'جنيه/كم'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Features/Amenities (if you have them) */}
              <div className="pt-4 border-t border-gray-dark">
                <p className="text-gray-silver text-xs uppercase tracking-wider mb-4">{language === 'en' ? 'Vehicle Details' : 'تفاصيل السيارة'}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 text-gray-light">
                    <div className="w-8 h-8 rounded-lg bg-charcoal flex items-center justify-center">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <span className="text-sm">{language === 'en' ? 'Licensed & Insured' : 'مرخص ومؤمن'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-light">
                    <div className="w-8 h-8 rounded-lg bg-charcoal flex items-center justify-center">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-sm">{language === 'en' ? 'Professional Driver' : 'سائق محترف'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Footer */}
            <div className="p-6 lg:p-8 border-t border-gray-dark bg-charcoal/30">
              <div className="flex gap-3">
                <Link
                  to="/book"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-primary to-primary-light text-blackC font-semibold rounded-xl hover:shadow-lg hover:shadow-primary/30 transition-all group"
                  onClick={() => setSelectedCar(null)}
                >
                  <span>{t.book}</span>
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <button
                  onClick={() => setSelectedCar(null)}
                  className="px-6 py-4 bg-charcoal border border-gray-dark text-gray-light rounded-xl hover:bg-gray-dark hover:text-cream hover:border-gray-light/30 transition-all"
                >
                  {language === 'en' ? 'Close' : 'إغلاق'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarModal;